import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { getDateLocale } from "@/lib/dateUtils";
import { motion } from "framer-motion";
import {
  UserCircle2,
  Loader2,
  Camera,
  ShieldCheck,
  ShieldAlert,
  Calendar,
  Mail,
  Phone,
  MapPin,
  FileText,
  ImagePlus,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

import maleAvatarImg from "@/assets/avatar-male.png";
import femaleAvatarImg from "@/assets/avatar-female.png";

import { tAuto } from "@/lib/tAuto";
import { hasProfileSubmittedKyc, isProfileKycVerified, isProfileVerificationPending } from "@/lib/kycStatus";
const maleAvatarUrl = maleAvatarImg;
const femaleAvatarUrl = femaleAvatarImg;

export function HostProfile() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const avatarInputRef = useRef<HTMLInputElement>(null);
  
  const [showGenderDialog, setShowGenderDialog] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);

  // Fetch user profile
  const { data: profile, isLoading } = useQuery({
    queryKey: ["profile", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  // Update profile mutation
  const updateProfile = useMutation({
    mutationKey: ["update-profile-host", user?.id],
    mutationFn: async (updates: any) => {
      const { error } = await supabase
        .from("profiles")
        .update(updates)
        .eq("id", user?.id);
      
      if (error) throw error;
      return updates;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile", user?.id] });
      queryClient.invalidateQueries({ queryKey: ["user-profile", user?.id] });
    },
    onError: () => {
      toast({
        title: "Erro",
        description: tAuto("auto.components_profile_hostprofile.1", "Não foi possível atualizar o perfil."),
        variant: "destructive",
      });
    },
  });

  const handleGenderSelect = async (gender: "male" | "female") => {
    const avatarUrl = gender === "male" ? maleAvatarUrl : femaleAvatarUrl;
    
    await updateProfile.mutateAsync({
      gender,
      avatar_url: avatarUrl,
    });
    
    setShowGenderDialog(false);
  };

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user?.id) return;

    if (!file.type.startsWith('image/')) {
      toast({
        title: tAuto("auto.components_profile_hostprofile.2", "Ficheiro inválido"),
        description: "Por favor, selecione uma imagem.",
        variant: "destructive",
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "Ficheiro muito grande",
        description: tAuto("auto.components_profile_hostprofile.3", "A imagem deve ter no máximo 5MB."),
        variant: "destructive",
      });
      return;
    }

    setIsUploadingAvatar(true);

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/avatar-${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName);

      await updateProfile.mutateAsync({
        avatar_url: publicUrl,
        gender: null,
      });

      setShowGenderDialog(false);
    } catch (error) {
      console.error('Error uploading avatar:', error);
      toast({
        title: "Erro no upload",
        description: tAuto("auto.components_profile_hostprofile.4", "Não foi possível carregar a imagem."),
        variant: "destructive",
      });
    } finally {
      setIsUploadingAvatar(false);
      if (avatarInputRef.current) {
        avatarInputRef.current.value = '';
      }
    }
  };

  const getAvatarUrl = () => {
    if (profile?.avatar_url) return profile.avatar_url;
    if (profile?.gender === "male") return maleAvatarUrl;
    if (profile?.gender === "female") return femaleAvatarUrl;
    return null;
  };

  const getInitials = () => {
    const name = profile?.full_name || user?.user_metadata?.full_name || "";
    return name.split(" ").map((n: string) => n[0]).join("").toUpperCase().slice(0, 2);
  };

  if (isLoading) {
    return (
      <div className="h-60 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const isVerified = isProfileKycVerified(profile);
  const hasSubmittedVerification = hasProfileSubmittedKyc(profile);
  const verificationPending = isProfileVerificationPending(profile);

  return (
    <div className="space-y-6">
      {/* Title Block */}
      <div className="flex items-center gap-3.5 mb-6">
        <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#E8513D]/15 to-[#D94F30]/5 flex items-center justify-center shadow-sm">
          <UserCircle2 className="w-5 h-5 text-[#E8513D]" />
        </div>
        <div>
          <h2 className="text-base font-bold text-gray-800 dark:text-zinc-100 font-display">{tAuto("auto.components_profile_hostprofile.5", "Perfil de Proprietário")}</h2>
          <p className="text-xs text-zinc-450 dark:text-zinc-550 font-semibold mt-0.5">{tAuto("auto.components_profile_hostprofile.6", "Veja e gerencie suas informações de conta no BeraStays")}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Left Card: Avatar & Summary */}
        <div className="lg:col-span-4 space-y-6">
          <Card className="rounded-3xl border border-gray-150 dark:border-zinc-800/40 overflow-hidden shadow-sm bg-white dark:bg-[#151518]/60">
            <div className="h-20 bg-gradient-to-br from-[#E8513D]/10 via-[#E8513D]/5 to-transparent" />
            <CardContent className="pt-0 -mt-12 pb-6">
              <div className="flex flex-col items-center text-center">
                <Dialog open={showGenderDialog} onOpenChange={setShowGenderDialog}>
                  <DialogTrigger asChild>
                    <button className="relative group cursor-pointer mb-4">
                      <Avatar className="h-24 w-24 border-4 border-white dark:border-zinc-900 shadow-xl ring-2 ring-[#E8513D]/10">
                        {getAvatarUrl() ? (
                          <AvatarImage src={getAvatarUrl()!} alt="Avatar" className="object-cover" />
                        ) : null}
                        <AvatarFallback className="bg-gradient-to-br from-[#E8513D] to-[#D14430] text-white font-bold text-2xl">
                          {getInitials()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <Camera className="w-6 h-6 text-white" />
                      </div>
                      {isVerified && (
                        <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center border-2 border-white dark:border-zinc-900 shadow-md">
                          <ShieldCheck className="w-4 h-4 text-white" />
                        </div>
                      )}
                    </button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md p-6 rounded-3xl border border-gray-100 dark:border-zinc-800">
                    <DialogHeader className="pb-4">
                      <DialogTitle className="text-center text-lg font-bold font-display">Escolha seu Avatar</DialogTitle>
                    </DialogHeader>

                    <input ref={avatarInputRef} type="file" accept="image/*" onChange={handleAvatarUpload} className="hidden" />

                    <div className="grid grid-cols-2 gap-4">
                      <button
                        onClick={() => handleGenderSelect("male")}
                        className={`aspect-square rounded-2xl border flex flex-col items-center justify-center gap-3 p-4 transition-all duration-200 ${
                          profile?.gender === "male" ? "border-[#E8513D] bg-[#E8513D]/5 shadow-sm" : "border-gray-100 dark:border-zinc-800 hover:border-[#E8513D]/30"
                        }`}
                      >
                        <div className="w-16 h-16 rounded-full overflow-hidden shadow-sm ring-1 ring-gray-100">
                          <img src={maleAvatarUrl} alt="Masculino" className="w-full h-full object-cover" />
                        </div>
                        <p className="text-xs font-bold text-gray-800 dark:text-zinc-200">Masculino</p>
                      </button>
                      <button
                        onClick={() => handleGenderSelect("female")}
                        className={`aspect-square rounded-2xl border flex flex-col items-center justify-center gap-3 p-4 transition-all duration-200 ${
                          profile?.gender === "female" ? "border-[#E8513D] bg-[#E8513D]/5 shadow-sm" : "border-gray-100 dark:border-zinc-800 hover:border-[#E8513D]/30"
                        }`}
                      >
                        <div className="w-16 h-16 rounded-full overflow-hidden shadow-sm ring-1 ring-gray-100">
                          <img src={femaleAvatarUrl} alt="Feminino" className="w-full h-full object-cover" />
                        </div>
                        <p className="text-xs font-bold text-gray-800 dark:text-zinc-200">Feminino</p>
                      </button>
                    </div>

                    <button
                      onClick={() => avatarInputRef.current?.click()}
                      disabled={isUploadingAvatar}
                      className="w-full mt-4 p-3 rounded-2xl border border-dashed border-[#E8513D]/30 hover:border-[#E8513D]/60 hover:bg-[#E8513D]/[0.02] flex items-center justify-center gap-2 transition-all"
                    >
                      {isUploadingAvatar ? (
                        <>
                          <Loader2 className="w-4 h-4 text-[#E8513D] animate-spin" />
                          <span className="text-xs font-bold text-[#E8513D]">A carregar...</span>
                        </>
                      ) : (
                        <>
                          <ImagePlus className="w-4 h-4 text-[#E8513D]" />
                          <span className="text-xs font-bold text-[#E8513D]">Carregar foto personalizada</span>
                        </>
                      )}
                    </button>
                  </DialogContent>
                </Dialog>

                <h3 className="text-base font-bold text-gray-850 dark:text-zinc-100 mb-1">{profile?.full_name || "Proprietário"}</h3>
                <p className="text-xs text-zinc-400 mb-4">{user?.email}</p>

                {isVerified ? (
                  <Badge className="bg-emerald-50 text-emerald-600 border border-emerald-100 dark:bg-emerald-950/20 dark:text-emerald-400 dark:border-emerald-900/30 gap-1.5 px-3 py-1 rounded-full uppercase tracking-wider text-[9px] font-extrabold">
                    <ShieldCheck className="w-3.5 h-3.5" />
                    Conta Verificada
                  </Badge>
                ) : verificationPending ? (
                  <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50 gap-1.5 px-3 py-1 rounded-full uppercase tracking-wider text-[9px] font-extrabold animate-pulse">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Em Análise
                  </Badge>
                ) : (
                  <div className="relative inline-flex">
                    <span className="absolute inset-0 rounded-full bg-primary/30 animate-ping" />
                    <Badge
                      variant="outline"
                      role="button"
                      tabIndex={0}
                      onClick={() => navigate("/perfil", { state: { openKyc: true } })}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate("/perfil", { state: { openKyc: true } }); } }}
                      className="relative text-red-500 border-red-200 bg-red-50 dark:bg-red-950/20 dark:text-red-400 dark:border-red-900/30 gap-1.5 px-3 py-1 rounded-full uppercase tracking-wider text-[9px] font-extrabold cursor-pointer hover:border-primary hover:bg-primary/10 hover:text-primary transition-colors animate-pulse"
                    >
                      <ShieldAlert className="w-3.5 h-3.5" />
                      Não verificada
                    </Badge>
                  </div>
                )}

                <Separator className="my-5 border-gray-100 dark:border-zinc-800/40" />

                <div className="text-center text-xs text-zinc-450 dark:text-zinc-550 font-semibold">
                  Membro desde{" "}
                  <span className="font-bold text-zinc-700 dark:text-zinc-350">
                    {profile?.created_at ? format(new Date(profile.created_at), "MMMM 'de' yyyy", { locale: getDateLocale() }) : "-"}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Card: Details */}
        <div className="lg:col-span-8">
          <Card className="rounded-3xl border border-gray-150 dark:border-zinc-800/40 shadow-sm bg-white dark:bg-[#151518]/60">
            <CardHeader className="pb-4 px-6 border-b border-gray-100 dark:border-zinc-800/40">
              <CardTitle className="text-sm font-bold font-display text-gray-800 dark:text-zinc-100">{tAuto("auto.components_profile_hostprofile.7", "Informações Pessoais")}</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                <div className="space-y-1">
                  <Label className="text-zinc-400 text-[10px] font-bold flex items-center gap-1.5 uppercase tracking-wider">
                    <UserCircle2 className="w-3.5 h-3.5 text-zinc-400" />
                    Nome Completo
                  </Label>
                  <p className="text-sm font-bold text-zinc-700 dark:text-zinc-200">{profile?.full_name || "-"}</p>
                </div>
                
                <div className="space-y-1">
                  <Label className="text-zinc-400 text-[10px] font-bold flex items-center gap-1.5 uppercase tracking-wider">
                    <Mail className="w-3.5 h-3.5 text-zinc-400" />
                    Email
                  </Label>
                  <p className="text-sm font-bold text-zinc-700 dark:text-zinc-200 truncate">{profile?.email || user?.email}</p>
                </div>

                <div className="space-y-1">
                  <Label className="text-zinc-400 text-[10px] font-bold flex items-center gap-1.5 uppercase tracking-wider">
                    <Phone className="w-3.5 h-3.5 text-zinc-400" />
                    Telefone
                  </Label>
                  <p className="text-sm font-bold text-zinc-700 dark:text-zinc-200">{profile?.phone || "-"}</p>
                </div>

                <div className="space-y-1">
                  <Label className="text-zinc-400 text-[10px] font-bold flex items-center gap-1.5 uppercase tracking-wider">
                    <MapPin className="w-3.5 h-3.5 text-zinc-400" />
                    Localização
                  </Label>
                  <p className="text-sm font-bold text-zinc-700 dark:text-zinc-200">
                    {profile?.city && profile?.country ? `${profile.city}, ${profile.country}` : "-"}
                  </p>
                </div>

                <div className="space-y-1">
                  <Label className="text-zinc-400 text-[10px] font-bold flex items-center gap-1.5 uppercase tracking-wider">
                    <FileText className="w-3.5 h-3.5 text-zinc-400" />
                    Tipo de Conta
                  </Label>
                  <p className="text-sm font-bold text-zinc-700 dark:text-zinc-200 capitalize">{profile?.account_type || "-"}</p>
                </div>

                {profile?.birth_date && (
                  <div className="space-y-1">
                    <Label className="text-zinc-400 text-[10px] font-bold flex items-center gap-1.5 uppercase tracking-wider">
                      <Calendar className="w-3.5 h-3.5 text-zinc-400" />
                      Data de Nascimento
                    </Label>
                    <p className="text-sm font-bold text-zinc-700 dark:text-zinc-200">
                      {format(new Date(profile.birth_date), "dd 'de' MMMM 'de' yyyy", { locale: getDateLocale() })}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
